import { startTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineMagnifyingGlass,
  HiOutlineBars3,
  HiOutlinePlus,
  HiOutlineSun,
  HiOutlineMoon,
  HiOutlineUser,
  HiOutlineArrowRightOnRectangle,
  HiOutlineCog6Tooth,
  HiOutlineUserPlus,
  HiOutlineExclamationTriangle,
  HiOutlineCalendarDays,
  HiOutlineBriefcase,
} from 'react-icons/hi2'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { NotificationCenter } from './NotificationCenter'
import { Avatar } from '@/components/ui/Avatar'
import { Dropdown, DropdownItem, DropdownLabel, DropdownSeparator } from '@/components/ui/Dropdown'
import { ROLE_LABELS } from '@/constants/rbac'
import { cn } from '@/utils/cn'

export function Topbar() {
  const setMobileNav = useUIStore((s) => s.setMobileNav)
  const setCommandOpen = useUIStore((s) => s.setCommandOpen)
  const theme = useUIStore((s) => s.theme)
  const toggleTheme = useUIStore((s) => s.toggleTheme)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()

  const quickAdds = [
    { label: 'New Contact', icon: <HiOutlineUserPlus />, to: '/contacts?new=1' },
    { label: 'Log Grievance', icon: <HiOutlineExclamationTriangle />, to: '/issues?new=1' },
    { label: 'Schedule Meeting', icon: <HiOutlineCalendarDays />, to: '/meetings?new=1' },
    { label: 'Add Opportunity', icon: <HiOutlineBriefcase />, to: '/opportunities?new=1' },
  ]

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-card/90 px-4 backdrop-blur-md">
      <button
        onClick={() => setMobileNav(true)}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-muted lg:hidden"
        aria-label="Open menu"
      >
        <HiOutlineBars3 className="h-5 w-5" />
      </button>

      {/* Global search trigger */}
      <button
        onClick={() => setCommandOpen(true)}
        className="flex h-10 w-full max-w-md items-center gap-2.5 rounded-lg border border-border bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
      >
        <HiOutlineMagnifyingGlass className="h-4 w-4" />
        <span className="hidden sm:inline">Search everything…</span>
        <span className="ml-auto hidden items-center gap-1 sm:flex">
          <span className="kbd">Ctrl</span>
          <span className="kbd">K</span>
        </span>
      </button>

      <div className="ml-auto flex items-center gap-1">
        {/* Quick Add */}
        <Dropdown
          align="right"
          trigger={
            <button className="flex h-10 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-800">
              <HiOutlinePlus className="h-4 w-4" />
              <span className="hidden sm:inline">Quick Add</span>
            </button>
          }
        >
          <DropdownLabel>Create new</DropdownLabel>
          {quickAdds.map((qa) => (
            <DropdownItem key={qa.label} icon={qa.icon} onClick={() => navigate(qa.to)}>
              {qa.label}
            </DropdownItem>
          ))}
        </Dropdown>

        <button
          onClick={toggleTheme}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Toggle theme"
        >
          {theme === 'light' ? <HiOutlineMoon className="h-5 w-5" /> : <HiOutlineSun className="h-5 w-5" />}
        </button>

        <NotificationCenter />

        {/* Profile */}
        <Dropdown
          align="right"
          width="w-64"
          trigger={
            <button className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-muted">
              <Avatar name={user?.name ?? 'User'} src={user?.avatarUrl} size="sm" color="#2FB350" />
              <span className="hidden text-left md:block">
                <span className="block max-w-[140px] truncate text-sm font-semibold leading-tight">
                  {user?.name}
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  {user ? ROLE_LABELS[user.role] : ''}
                </span>
              </span>
            </button>
          }
        >
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar name={user?.name ?? 'User'} src={user?.avatarUrl} size="md" color="#2FB350" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <DropdownSeparator />
          <DropdownItem icon={<HiOutlineUser />} onClick={() => navigate('/settings/profile')}>
            My Profile
          </DropdownItem>
          <DropdownItem icon={<HiOutlineCog6Tooth />} onClick={() => navigate('/settings')}>
            Settings
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem
            icon={<HiOutlineArrowRightOnRectangle />}
            danger
            onClick={() => {
              startTransition(() => {
                logout()
                navigate('/login')
              })
            }}
          >
            Sign out
          </DropdownItem>
        </Dropdown>
      </div>
    </header>
  )
}
